import type { ReactNode } from 'react';

import classNames from 'classnames';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import Button from '../../../../../components/buttons/Button';
import { useLimitedOfferStock } from '../../../../../hooks/useLimitedOfferStock';
import type { EventTriggerPopupData } from '../../../../../stores/event-trigger/types';
import { WidgetStockCounter } from '../WidgetStockCounter/WidgetStockCounter';

import styles from './LimitedOfferWideWidget.module.scss';

interface LimitedOfferWideWidgetProps {
  event: EventTriggerPopupData;
  widgetImageUrl: string;
  timer?: ReactNode;
  /** CTA button label — defaults to the "VIEW" translation. */
  ctaLabel?: string;
}

/**
 * The wide lobby widget of a Limited Offer: the stock rides a tab over the CTA's top edge, the same
 * treatment the offer popup gives its claim button, so the widget and the offer it opens read alike.
 * Once sold out the widget takes the disabled colours, the way the popup's claim button does.
 *
 * An observer because the sold-out flip arrives on the event-trigger tick, and nothing else in this
 * subtree would re-render on it.
 */
export const LimitedOfferWideWidget = observer((props: LimitedOfferWideWidgetProps) => {
  const { event, widgetImageUrl, timer, ctaLabel } = props;
  const { t } = useTranslation();
  const limitedOfferStock = useLimitedOfferStock(event);

  return (
    <div className={classNames(styles.container, { [styles.containerSoldOut]: limitedOfferStock?.isSoldOut })}>
      <div className={styles.bannerBase} />
      <span className={styles.banner}>
        <WidgetStockCounter event={event} />
      </span>
      <img alt="limit offer widget" className={styles.img} src={widgetImageUrl} />
      <div className={styles.wrapper}>
        {timer}
        <div className={styles.capsule}>
          <Button className={styles.btn} dataTestId="limited-offer-wide-widget-btn">
            {ctaLabel ?? t('common.view')}
          </Button>
        </div>
      </div>
    </div>
  );
});
